// A record page of an object described by the schema (config/objects kit
// schemaRecord): its Details fields shown by their type in /meta, lookups
// linked to the related record, and every column the page does not place, or
// hide, added in Other Fields before System. So a new column shows up on the
// record page with no code.
import { type FieldDef, labelFromKey, type ObjectMetadata } from '@olytic/foundation';

import { DETAIL_FORMATS, field, SCHEMA_RECORDS } from '../config/objects/kit';
import type { ObjectKey } from '../navigation';
import type { ObjectMeta, ObjectRow } from './objectsApi';

/** The section new fields land in. */
export const OTHER_FIELDS = { key: 'other', label: 'Other Fields' } as const;

export function withSchemaDetails(
  object: ObjectKey,
  metadata: ObjectMetadata<ObjectRow>,
  meta: Pick<ObjectMeta, 'columns' | 'fields'> | null,
): ObjectMetadata<ObjectRow> {
  const schema = SCHEMA_RECORDS.get(object);
  const columns = meta?.columns ?? [];
  if (!schema || columns.length === 0) return metadata;
  const columnOf = new Map(columns.map((c) => [c.key, c]));
  const details = (metadata.details ?? []).map((detail): FieldDef<ObjectRow> => {
    const column = columnOf.get(detail.key);
    if (!schema.typed.has(detail.key) || !column) return detail;
    const format = DETAIL_FORMATS[column.type];
    return {
      ...detail,
      render: (row) => format(row[detail.key]),
      ...(column.lookup ? { link: { object: column.lookup.object as ObjectKey, idKey: column.lookup.idKey } } : {}),
    };
  });
  const placed = new Set(details.map((d) => d.key));
  const added = columns
    .filter((c) => !placed.has(c.key) && !schema.hidden.has(c.key) && c.key !== 'id' && !c.key.endsWith('_id'))
    .map((c) => {
      const format = DETAIL_FORMATS[c.type];
      return {
        ...field(c.key, labelFromKey(c.key), { format }),
        ...(c.lookup ? { link: { object: c.lookup.object as ObjectKey, idKey: c.lookup.idKey } } : {}),
      };
    })
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  if (added.length === 0) return { ...metadata, details };
  const sections = [...(metadata.sections ?? [])];
  const system = sections.findIndex((s) => s.key === 'system');
  sections.splice(system === -1 ? sections.length : system, 0, { ...OTHER_FIELDS, fields: added.map((d) => d.key) });
  return { ...metadata, details: [...details, ...added], sections };
}
